import Error, { ErrorType } from "@components/global/new/Error/Error";
import { Input } from "@components/global/new/Input/Input";
import Title from "@components/global/new/Title/Title";
import WithModal, { CommonModalPropsI } from "@components/HOC/withModals";
import { MODALS_ENUM } from "@constants/Modals";
import mainLocationsStore from "@mobx/store/locations";
import { ILocationStrings } from "@mobx/store/locations/types";
import { ILocationForList } from "@services/locations";
import { useGlobalNotifs } from "@utils/hooks/useGlobalNotifs";
import { isCurrentStr } from "@utils/isRussianLang";
import { makeNotifs } from "@utils/makeNotifs";
import { Button } from "antd";
import cn from "classnames";
import { LatLngExpression } from "leaflet";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import CoordsMap3 from "../CoordsMap3";
import styles from "./AddLocation.module.scss";

interface Elems3 extends HTMLFormControlsCollection {
  name: HTMLInputElement;
  loc_id: HTMLInputElement;
  address: HTMLInputElement;
  coords: HTMLInputElement;
  notes: HTMLInputElement;
}

interface MyForm3 extends HTMLFormElement {
  readonly elements: Elems3;
}

const useControl = (
  props: IAddLocationModal3Props
) => {
  const notifs = makeNotifs();
  const { onError } = useGlobalNotifs();
  const [loading, setLoading] = useState<boolean>(false);
  const [showEx, setShowEx] = useState<boolean>(false);
  const [errorsStr, setErrorsStr] = useState<ErrorType>();
  const [openMapModal, setOpenMapModal] = useState<boolean>(false);
  const [mapCoords, setMapCoords] = useState<LatLngExpression>(
    props.location?.Coord?.split(",").map((item) => +item) as LatLngExpression
  );
  
  const locations = mainLocationsStore.locationsInOrder;
  const locationStore = props.location?.Id ? mainLocationsStore.locations[+props.location.Id] : undefined;

  const onSubmit = async (e: React.FormEvent<MyForm3>) => {
    e.preventDefault();
    setLoading(true);
    const { name, loc_id, address, notes } = e.currentTarget.elements;

    const elems = { name, loc_id, address, notes };

    for (let key in { name, loc_id }) {
      const el = elems[key as keyof typeof elems] as HTMLInputElement;
      if (el.value.length === 0) {
        setErrorsStr((prev) => ({
          ...prev,
          fill: true,
        }));
        setLoading(false);
        return;
      }
    }

    setErrorsStr(undefined);

    const values: Omit<ILocationStrings, "Id"> = {
      Name: name.value,
      Code: loc_id.value,
      Address: address.value,
      Coord: mapCoords?.toString(),
      Notes: notes.value,
      Uuid: "",
    };
    if (!isCurrentStr(values.Code)) {
      setErrorsStr({ ownError: [notifs.errors.symbols] });
      return;
    }

    const edit = props.location === undefined ? false : true;

    const isSameId = edit
      ? false
      : locations.find((item) => item.location.Code === loc_id.value)
        ? true
        : false;

    if (isSameId) {
      onError(notifs.errors.locations__sameId);
      setLoading(false);
      return;
    }

    const resp = edit
      ? await locationStore?.edit(values)
      : await mainLocationsStore.addLocation(values);

    setLoading(false);
    if (!resp) return;
    
    props.handleClose();
  };

  return {
    loading,
    showEx,
    errorsStr,
    openMapModal,
    mapCoords,

    onSubmit,
    setShowEx,
    setOpenMapModal,
    setMapCoords,
  };
};

export interface IAddLocationModal3Props extends CommonModalPropsI {
  location?: ILocationForList;
}

const AddLocationModal3 = observer((props: IAddLocationModal3Props) => {
  const control = useControl(props);
  const formatMessage = props.formatMessage;
  return (
    <div className={styles.wrapper}>
      <CoordsMap3
        coords={control.mapCoords}
        getCoords={control.setMapCoords}
        openMapModal={control.openMapModal}
        setOpenMapModal={control.setOpenMapModal}
      />
      <Title>
        {formatMessage({
          id: !props.location ? "modal.location.title" : "modal.location.title_edit",
        })}
      </Title>

      <form className={styles.content} onSubmit={control.onSubmit}>
        <Input
          label={formatMessage({ id: "modal.location.name" })}
          type='text'
          id='name'
          value={props.location?.Name}
        />
        <Input
          label={formatMessage({ id: "modal.location.id" })}
          type='text'
          id='loc_id'
          value={props.location?.Code}
          disabled={props.location?.Code ? true : false}
          readOnly={props.location?.Code ? true : false}
        />

        <Input
          label={formatMessage({ id: "modal.location.address" })}
          type='text'
          id='address'
          value={props.location?.Address}
        />
        <div className={styles.coords}>
          <div className={styles.label_wrapper}>
            <label
              className={cn(styles.label, {})}
            >
              {formatMessage({ id: "modal.location.coords" })}
            </label>
            <span
              onClick={() => control.setOpenMapModal(true)}
              className={styles.map}
            />
          </div>
          <p>{control.mapCoords?.toString()}</p>
        </div>

        <div className={styles.area}>
          <label>{formatMessage({ id: "modal.location.notes" })}</label>
          <textarea id='notes' defaultValue={props.location?.Notes} />
        </div>

        {control.errorsStr && <Error errors={control.errorsStr} />}

        <div className={styles.btns}>
          <Button onClick={props.handleClose} htmlType='button'>
            {formatMessage({ id: "modal.location.btn.cancel" })}
          </Button>
          <Button htmlType='submit' type="primary">
            {formatMessage({ id: "modal.location.btn.save" })}
          </Button>
        </div>
      </form>
    </div>
  );
});

AddLocationModal3.displayName = "AddLocationModal3";

export default WithModal(MODALS_ENUM.ADD_LOCAION_MOBX, AddLocationModal3);
